<template>
  <div :class="['template-designer-v2', { 'embedded-mode': embedded }]">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <a-button @click="handleReset" icon="undo">
          恢复默认模板
        </a-button>
      </div>
      
      <div class="toolbar-center">
        <!-- 纸张类型按钮组 -->
        <a-button-group>
          <a-button 
            v-for="(paper, key) in paperTypes" 
            :key="key"
            :type="curPaperType === key ? 'primary' : 'default'"
            @click="setPaper(key, paper)"
          >
            {{ key }}
          </a-button>
          <a-button :type="curPaperType === 'custom' ? 'primary' : 'default'" @click="showCustomPaperModal">
            自定义
          </a-button>
        </a-button-group>
        
        <!-- 缩放控制 -->
        <div class="zoom-control">
          <a-button icon="minus" @click="changeScale(false)" :disabled="scaleValue <= scaleMin" />
          <span class="scale-display">{{ Math.round(scaleValue * 100) }}%</span>
          <a-button icon="plus" @click="changeScale(true)" :disabled="scaleValue >= scaleMax" />
        </div>
      </div>
      
      <div class="toolbar-right">
        <a-button icon="eye" @click="handlePreview">打印预览</a-button>
        <a-button icon="reload" @click="handleRefresh">刷新</a-button>
        <!-- 对齐按钮组 - 纯图标，仅选中 ≥2 个元素时显示 -->
        <a-button-group v-if="selectedCount >= 2" class="align-btns">
          <a-tooltip title="左对齐"><a-button size="small" icon="align-left" @click="alignElements('left')" /></a-tooltip>
          <a-tooltip title="垂直居中"><a-button size="small" icon="align-center" @click="alignElements('vertical')" /></a-tooltip>
          <a-tooltip title="右对齐"><a-button size="small" icon="align-right" @click="alignElements('right')" /></a-tooltip>
          <a-divider type="vertical" />
          <a-tooltip title="顶部对齐"><a-button size="small" icon="vertical-align-top" @click="alignElements('top')" /></a-tooltip>
          <a-tooltip title="水平居中"><a-button size="small" icon="vertical-align-middle" @click="alignElements('horizontal')" /></a-tooltip>
          <a-tooltip title="底部对齐"><a-button size="small" icon="vertical-align-bottom" @click="alignElements('bottom')" /></a-tooltip>
          <a-divider type="vertical" />
          <a-tooltip title="水平均分"><a-button size="small" icon="column-width" @click="alignElements('distributeHor')" /></a-tooltip>
          <a-tooltip title="垂直均分"><a-button size="small" icon="column-height" @click="alignElements('distributeVer')" /></a-tooltip>
        </a-button-group>
        <a-tooltip title="网格设置">
          <a-button size="small" icon="border" @click="gridModalVisible = true" />
        </a-tooltip>
        <a-button icon="select" @click="selectAll">全选</a-button>
        <a-button type="primary" icon="save" @click="handleSave">保存</a-button>
        <a-button icon="delete" @click="handleClear">清空</a-button>
        <a-button icon="code" @click="showJsonModal">模板JSON</a-button>
        <a-button v-if="!embedded" icon="close" @click="handleBack">关闭</a-button>
      </div>
    </div>
    
    <!-- 自定义纸张弹窗 -->
    <a-modal
      :visible="customPaperModalVisible"
      title="自定义纸张大小"
      :footer="null"
      width="400px"
      @cancel="customPaperModalVisible = false"
      style="top: 200px;"
    >
      <div class="custom-paper-form">
        <div class="form-item">
          <label>宽度(mm)</label>
          <a-input-number v-model="paperWidth" :min="20" :max="1000" style="width: 150px;" />
        </div>
        <div class="form-item">
          <label>高度(mm)</label>
          <a-input-number v-model="paperHeight" :min="20" :max="1000" style="width: 150px;" />
        </div>
        <a-divider style="margin: 12px 0;">页边距</a-divider>
        <div class="form-item">
          <label>上边距(mm)</label>
          <a-input-number v-model="marginTop" :min="0" :max="100" @change="onMarginChange" style="width: 150px;" />
        </div>
        <div class="form-item">
          <label>下边距(mm)</label>
          <a-input-number v-model="marginBottom" :min="0" :max="100" @change="onMarginChange" style="width: 150px;" />
        </div>
        <div class="form-item">
          <label>左边距(mm)</label>
          <a-input-number v-model="marginLeft" :min="0" :max="100" @change="onMarginChange" style="width: 150px;" />
        </div>
        <div class="form-item">
          <label>右边距(mm)</label>
          <a-input-number v-model="marginRight" :min="0" :max="100" @change="onMarginChange" style="width: 150px;" />
        </div>
        <div class="form-actions">
          <a-button @click="customPaperModalVisible = false">取消</a-button>
          <a-button type="primary" @click="applyCustomPaper">确定</a-button>
        </div>
      </div>
    </a-modal>
    
    <!-- 网格设置弹窗 -->
    <a-modal
      :visible="gridModalVisible"
      title="网格设置"
      :footer="null"
      width="360px"
      @cancel="gridModalVisible = false"
    >
      <div class="grid-settings-form">
        <div class="form-item">
          <label>显示网格</label>
          <a-switch v-model="gridShow" @change="onGridSettingsChange" />
        </div>
        <div class="form-item">
          <label>网格大小(mm)</label>
          <a-input-number 
            v-model="gridSize" 
            :min="1" :max="50" :step="1"
            :disabled="!gridShow"
            @change="onGridSettingsChange"
            style="width: 150px;" 
          />
        </div>
      </div>
    </a-modal>
    
    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧字段面板 -->
      <div class="left-panel">
        <div class="panel-header">可用字段</div>
        <div class="panel-content">
          <!-- hiprint 字段容器 -->
          <div class="hiprintEpContainer"></div>
          
          <!-- 加载状态 -->
          <div v-if="!fieldPanelLoaded" class="loading-hint">
            <a-spin size="small" /> 加载字段中...
          </div>
        </div>
      </div>
      
      <!-- 中间画布区 -->
      <div class="center-panel">
        <!-- 标尺 -->
        <div class="ruler"></div>
        
        <!-- hiprint 设计画布 -->
        <div id="hiprint-printTemplate" class="hiprint-printTemplate"></div>
        
        <!-- hiprint分页容器（隐藏） -->
        <div class="hiprint-printPagination" style="display: none;"></div>
        
        <!-- 空状态 -->
        <div v-if="!templateLoaded" class="empty-state">
          <a-empty description="正在加载模板..." />
        </div>
      </div>
      
      <!-- 右侧属性面板 -->
      <div class="right-panel">
        <div class="panel-header">属性设置</div>
        <div class="panel-content">
          <!-- hiprint 属性设置容器 -->
          <div id="PrintElementOptionSetting" class="PrintElementOptionSetting"></div>
          
          <!-- 提示 -->
          <div v-if="selectedCount === 0" class="selection-hint">
            <a-icon type="info-circle" /> 点击画布上的元素以编辑属性
          </div>
        </div>
      </div>
    </div>
    
    <!-- JSON编辑弹窗 -->
    <a-modal
      :visible="jsonModalVisible"
      title="模板JSON编辑"
      width="700px"
      :footer="null"
      @cancel="jsonModalVisible = false"
    >
      <div class="json-modal-content">
        <div class="json-toolbar">
          <a-switch v-model="jsonCompressed" checked-children="压缩" un-checked-children="格式化" />
          <a-button type="link" size="small" @click="formatJson">格式化</a-button>
          <a-button type="link" size="small" @click="compressJson">压缩</a-button>
        </div>
        <a-textarea
          v-model="templateJsonText"
          :rows="20"
          placeholder="模板JSON内容"
        />
        <div class="json-modal-footer">
          <a-button @click="jsonModalVisible = false">取消</a-button>
          <a-button type="primary" @click="applyJson">应用</a-button>
        </div>
      </div>
    </a-modal>
    
    
  </div>
</template>

<script>
import { registerAllBillTypes, registerBillType, getProvidersArr, findProviderByType, initHiprint } from '@/services/hiprint-register'
import HiprintService from '@/services/hiprint-service'
import { getList, saveBillPrintTemplate, getStructuredFields, loadTemplateWithFallback, getTemplateByBillType } from '@/api/plugin/print-template'
import { 
  getDefaultTemplateByName, 
  getDefaultTemplateByCode, 
  getBillTypeName, 
  getBillTypeCode,
  getAllBillTypes,
  codeToNameMap 
} from '@/assets/print_template'
import { getBillTypeFromNo, normalizeBillType } from '@/utils/billTypeUtils'

export default {
  name: 'TemplateDesignerV2',
  
  props: {
    billType: {
      type: String,
      default: ''
    },
    billTypeName: {
      type: String,
      default: ''
    },
    // 新增：单据编号（用于智能识别类型）
    billNo: {
      type: String,
      default: ''
    },
    templateId: {
      type: [String, Number],
      default: null
    },
    // 新增：真实单据数据（用于预览）
    billData: {
      type: Object,
      default: null
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      // hiprint实例
      template: null,
      
      // 当前纸张配置
      curPaper: {
        type: 'A4',
        width: 210,
        height: 296.6
      },
      
      // 纸张类型
      paperTypes: {
        'A4': { width: 210, height: 296.6 },
        '二等分': { width: 241, height: 140 },
        '三等分': { width: 241, height: 93.3 },
        '四等分': { width: 241, height: 70 }
      },
      
      // 自定义纸张
      customPaperModalVisible: false,
      paperWidth: 210,
      paperHeight: 296.6,
      
      // 缩放
      scaleValue: 1,
      scaleMin: 0.5,
      scaleMax: 5,
      
      // 当前单据类型
      selectedBillType: '',
      
      // 模板相关
      selectedTemplate: null,
      templateTenantId: null,  // 当前加载模板的租户ID（null或0为系统模板）
      currentTemplateName: '',
      templateList: [],
      
      // UI状态
      jsonModalVisible: false,
      jsonCompressed: true,
      templateJsonText: '',
      selectedCount: 0,
      
      // 网格设置
      gridModalVisible: false,
      gridShow: false,
      gridSize: 8,  // 默认 8mm
      
      // 页边距设置
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      
      // 加载状态
      templateLoaded: false,
      fieldPanelLoaded: false
    }
  },
  
  computed: {
    curPaperType() {
      return this.curPaper.type
    }
  },
  
  watch: {
    billType: {
      handler(newVal, oldVal) {
        console.log('[TemplateDesignerV2] billType changed:', oldVal, '->', newVal)
        if (newVal && newVal !== oldVal) {
          this.reinitialize()
        }
      },
      immediate: false
    }
  },
  
  created() {
    console.log('[TemplateDesignerV2] created, billType:', this.billType, 'billTypeName:', this.billTypeName)
    this.initBillType()
  },
  
  mounted() {
    console.log('[TemplateDesignerV2] mounted')
    this.$nextTick(() => {
      this.initDesigner()
    })
  },
  
  beforeDestroy() {
    console.log('[TemplateDesignerV2] beforeDestroy')
    this.saveHeaderFooterToLocal()
    if (this.template) {
      try {
        if (this.template.destroy) {
          this.template.destroy()
        } else {
          this.template.clear()
        }
        this.template = null
      } catch (e) {
        console.warn('清理设计器失败', e)
      }
    }
  },
  
  methods: {
    /**
     * 重新初始化
     */
    async reinitialize() {
      console.log('[TemplateDesignerV2] reinitialize')
      
      // 销毁旧模板
      if (this.template) {
        try {
          if (this.template.destroy) {
            this.template.destroy()
          }
          this.template = null
        } catch (e) {
          console.warn('销毁旧模板失败', e)
        }
      }
      
      // 重置状态
      this.templateLoaded = false
      this.fieldPanelLoaded = false
      this.selectedCount = 0
      
      // 清空画布
      const container = document.getElementById('hiprint-printTemplate')
      if (container) {
        container.innerHTML = ''
      }
      
      // 重新初始化
      this.initBillType()
      await this.initDesigner()
    },
    
    /**
     * 初始化单据类型
     */
    initBillType() {
      let billTypeCode = this.billType
      
      console.log('[TemplateDesignerV2] initBillType, raw billType:', billTypeCode, 'billNo:', this.billNo)
      
      // 1. 如果有 billNo，优先从 billNo 识别
      if (!billTypeCode && this.billNo) {
        billTypeCode = getBillTypeFromNo(this.billNo)
        console.log('[TemplateDesignerV2] identified from billNo:', billTypeCode)
      }
      
      // 2. 如果没有，尝试转换
      if (billTypeCode && !codeToNameMap[billTypeCode]) {
        console.log('[TemplateDesignerV2] billType not in codeToNameMap, trying to convert')
        const code = getBillTypeCode(billTypeCode)
        console.log('[TemplateDesignerV2] converted code:', code)
        if (code !== billTypeCode) {
          billTypeCode = code
        }
      }
      
      // 3. 规范化
      if (billTypeCode) {
        billTypeCode = normalizeBillType(billTypeCode)
      }
      
      this.selectedBillType = billTypeCode || 'QGD'
      console.log('[TemplateDesignerV2] selectedBillType:', this.selectedBillType)
      
      // 如果有模板ID，加载对应模板
      if (this.templateId) {
        this.selectedTemplate = Number(this.templateId)
      }
    },
    
    /**
     * 初始化设计器
     * 使用 Provider 模式初始化 hiprint
     */
    async initDesigner() {
      console.log('[TemplateDesignerV2] initDesigner start, selectedBillType:', this.selectedBillType, 'billType prop:', this.billType)
      
      try {
        // 验证 hiprint 是否可用
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
        
        if (!hiprint) {
          console.error('[TemplateDesignerV2] hiprint not found!')
          this.$message.error('hiprint未加载，请刷新页面重试')
          return
        }
        
        // 初始化 hiprint 模块引用
        initHiprint(hiprint)
        
        console.log('[TemplateDesignerV2] hiprint available:', !!hiprint, 'PrintTemplate:', !!hiprint.PrintTemplate)
        
        // 查找匹配的 provider
        const providers = getProvidersArr()
        let matchedProvider = null
        
        // 尝试用 code 匹配 (如 "LSCK")
        matchedProvider = providers.find(p => p.value === this.selectedBillType)
        
        // 如果没找到，尝试用 type (中文全名) 匹配
        if (!matchedProvider) {
          matchedProvider = providers.find(p => p.type === this.billType)
        }
        
        if (!matchedProvider) {
          console.error('[TemplateDesignerV2] 未找到 Provider:', this.selectedBillType, this.billType)
          this.$message.error('未找到对应的单据类型配置')
          return
        }
        
        console.log('[TemplateDesignerV2] 使用 Provider:', matchedProvider.name, matchedProvider.value)
        
        // 初始化 hiprint (使用 provider)
        // 注意：hiprint.init 会调用 provider 的 addElementTypes 方法，并传入内部实例
        if (typeof hiprint.init === 'function') {
            hiprint.init({ 
                providers: [matchedProvider.f],
                socketUrl: false 
            })
            console.log('[TemplateDesignerV2] hiprint.init 成功，已注册元素类型')
        } else {
            console.error('[TemplateDesignerV2] hiprint.init 不存在！')
            this.$message.error('hiprint 初始化失败，请刷新页面重试')
            return
        }
        
        // 获取模板数据 - 使用两级回退
        let templateJsonData = null
        let loadedTemplateId = null
        let loadedTemplateName = null
        
        try {
          // 使用新的两级回退加载函数，返回 { id, templateName, templateJson }
          const userInfo = this.$store && (this.$store.getters.userInfo || (this.$store.state.user && this.$store.state.user.info))
          const tenantId = userInfo ? userInfo.tenantId : null
          const result = await loadTemplateWithFallback(this.selectedBillType, tenantId)
          console.log('[TemplateDesignerV2] loadTemplateWithFallback result:', result ? 'success' : 'empty')
          
          if (result) {
            // 提取模板ID（用于后续保存时更新）
            if (result.id) {
              loadedTemplateId = result.id
              this.selectedTemplate = result.id
            }
            // 提取模板的租户ID（用于判断保存时是新增还是更新）
            // tenantId 为 0 或 undefined 表示系统模板，保存时需要新增
            if (result.tenantId !== undefined) {
              this.templateTenantId = result.tenantId
            }
            console.log('[TemplateDesignerV2] 加载模板 tenantId:', this.templateTenantId, 'id:', result.id)
            // 提取模板名称
            if (result.templateName) {
              loadedTemplateName = result.templateName
            }
            // 提取模板JSON
            templateJsonData = result.templateJson || result
            
            // 从后端模板数据中恢复纸张和页边距
            if (result.id) {
              try {
                const fullData = await getTemplateByBillType(this.selectedBillType)
                if (fullData && fullData.code === 200 && fullData.data) {
                  const tpl = fullData.data
                  if (tpl.paperType) this.curPaper.type = tpl.paperType
                  if (tpl.paperWidth) this.curPaper.width = Number(tpl.paperWidth)
                  if (tpl.paperHeight) this.curPaper.height = Number(tpl.paperHeight)
                  if (tpl.marginTop !== undefined) this.marginTop = Number(tpl.marginTop) || 0
                  if (tpl.marginBottom !== undefined) this.marginBottom = Number(tpl.marginBottom) || 0
                  if (tpl.marginLeft !== undefined) this.marginLeft = Number(tpl.marginLeft) || 0
                  if (tpl.marginRight !== undefined) this.marginRight = Number(tpl.marginRight) || 0
                }
              } catch (e) {
                console.warn('[TemplateDesignerV2] 加载纸张/页边距数据失败:', e.message)
              }
            }
          }
        } catch (err) {
          console.warn('[TemplateDesignerV2] loadTemplateWithFallback failed:', err.message)
        }
        
        // 如果两级回退失败，使用旧方法
        if (!templateJsonData || !templateJsonData.panels) {
          // 1. 先尝试通过中文全名获取
          if (this.billType) {
            templateJsonData = getDefaultTemplateByName(this.billType)
            console.log('[TemplateDesignerV2] 1. getDefaultTemplateByName("' + this.billType + '"):', !!templateJsonData)
          }
          
          // 2. 如果没有，尝试通过 code 获取
          if (!templateJsonData && this.selectedBillType) {
            templateJsonData = getDefaultTemplateByCode(this.selectedBillType)
            console.log('[TemplateDesignerV2] 2. getDefaultTemplateByCode("' + this.selectedBillType + '"):', !!templateJsonData)
          }
          
          // 3. 如果还是没有，尝试通过 billTypeName prop 获取
          if (!templateJsonData && this.billTypeName) {
            templateJsonData = getDefaultTemplateByName(this.billTypeName)
            console.log('[TemplateDesignerV2] 3. getDefaultTemplateByName("' + this.billTypeName + '"):', !!templateJsonData)
          }
        }
        
        if (!templateJsonData) {
          console.warn('[TemplateDesignerV2] No template found! will use empty template')
          this.$message.warning('未找到默认模板，将使用空模板')
          templateJsonData = { panels: [] }
        } else {
          console.log('[TemplateDesignerV2] Template found successfully, panels:', templateJsonData.panels && templateJsonData.panels.length)
          // 设置模板名称：优先使用加载的名称，否则使用 Provider 名称
          this.currentTemplateName = loadedTemplateName || matchedProvider.name + '（默认）'
        }
        
        // 初始化 hiprint 设计器
        this.initHiprintDesigner(templateJsonData)
        
      } catch (error) {
        console.error('[TemplateDesignerV2] 初始化设计器失败', error)
        this.$message.error('初始化设计器失败: ' + error.message)
      }
    },
    
    /**
     * 解析模板JSON
     */
    parseTemplateJson(templateJson) {
      if (!templateJson) return null
      try {
        return typeof templateJson === 'string' ? JSON.parse(templateJson) : templateJson
      } catch (e) {
        console.warn('解析模板JSON失败', e)
        return null
      }
    },
    
    /**
     * 加载模板列表
     */
    async loadTemplateList() {
      try {
        // 直接使用 billType code 查询后端（如 CGDD）
        const res = await getList({ billType: this.selectedBillType, size: 100 })
        
        if (res && res.code === 200) {
          this.templateList = (res.data && (res.data.list || res.data.records)) || []
        }
      } catch (error) {
        console.warn('加载模板列表失败', error)
        this.templateList = []
      }
    },
    
    /**
     * 初始化hiprint设计器
     */
    initHiprintDesigner(templateData) {
      console.log('[TemplateDesignerV2] initHiprintDesigner, templateData:', !!templateData)
      
      const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
      
      if (!hiprint || !hiprint.PrintTemplate) {
        console.error('[TemplateDesignerV2] hiprint.PrintTemplate not found!')
        this.$message.error('hiprint未加载，请刷新页面重试')
        return
      }
      
      try {
        console.log('[TemplateDesignerV2] Creating PrintTemplate instance...')
        
        // 创建PrintTemplate实例
        this.template = new hiprint.PrintTemplate({
          template: templateData || { panels: [] },
          dataMode: 1,
          history: false,
          onDataChanged: () => {},
          settingContainer: '#PrintElementOptionSetting',
          paginationContainer: '.hiprint-printPagination'
        })
        
        console.log('[TemplateDesignerV2] PrintTemplate instance created')
        
        // 进入设计模式
        this.template.design('#hiprint-printTemplate')
        console.log('[TemplateDesignerV2] design() called')
        
        // 标记模板已加载
        this.$nextTick(() => {
          this.templateLoaded = true
          console.log('[TemplateDesignerV2] templateLoaded = true')
          
          // 构建字段面板
          this.buildFieldPanel()
          
          // 设置选择监听
          this.setupElementSelection()
          
          // 自动选中第一个元素
          this.autoSelectFirstElement()
          
          // 应用网格覆盖层
          this.loadGridSettingsFromLocal()
          this.applyGridOverlay()
          this.applyMarginOverlay()
          
          // 设置默认页眉页脚为 15mm（优先从本地加载）
          const loadedFromLocal = this.loadHeaderFooterFromLocal()
          if (!loadedFromLocal) {
            const headerPt = Math.round(15 * 2.8346)  // 15mm ≈ 42.5pt
            if (this.template && this.template.editingPanel) {
              const panel = this.template.editingPanel
              if (!panel.paperHeader || panel.paperHeader === 0) {
                panel.paperHeader = headerPt
                if (panel.designPaper && panel.designPaper.headerLinetarget) {
                  panel.designPaper.headerLinetarget.css('top', headerPt + 'pt')
                  panel.designPaper.headerLinetarget.removeClass('hideheaderLinetarget')
                }
              }
              if (panel.paperFooter === panel.height || !panel.paperFooter) {
                panel.paperFooter = panel.height - headerPt
                if (panel.designPaper && panel.designPaper.footerLinetarget) {
                  panel.designPaper.footerLinetarget.css('top', panel.paperFooter + 'pt')
                  panel.designPaper.footerLinetarget.removeClass('hidefooterLinetarget')
                }
              }
            }
          }
        })
        
      } catch (error) {
        console.error('[TemplateDesignerV2] 初始化hiprint设计器失败', error)
        this.$message.error('初始化hiprint设计器失败: ' + error.message)
      }
    },
    
    /**
     * 构建字段面板 - 使用 Provider 方式
     */
    buildFieldPanel() {
      console.log('[TemplateDesignerV2] buildFieldPanel, selectedBillType:', this.selectedBillType)
      
      const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
      
      if (!hiprint) {
        console.warn('[TemplateDesignerV2] hiprint not available for buildFieldPanel')
        return
      }
      
      const container = document.querySelector('.hiprintEpContainer')
      if (!container) {
        console.warn('[TemplateDesignerV2] .hiprintEpContainer not found')
        return
      }
      
      // 清空容器
      container.innerHTML = ''
      
      try {
        // 查找 provider
        const providers = getProvidersArr()
        let matchedProvider = providers.find(p => p.value === this.selectedBillType)
        
        if (!matchedProvider) {
          matchedProvider = providers.find(p => p.type === this.billType)
        }
        
        if (matchedProvider) {
          console.log('[TemplateDesignerV2] Found provider:', matchedProvider.name, matchedProvider.value)
          
          // 使用 hiprint 内置的方法构建字段面板
          // 注意：这里不再需要再次调用 addElementTypes，因为已经在 initDesigner 中通过 hiprint.init 完成了
          if (hiprint.PrintElementTypeManager && typeof hiprint.PrintElementTypeManager.build === 'function') {
            console.log('[TemplateDesignerV2] Using PrintElementTypeManager.build with key:', matchedProvider.value)
            hiprint.PrintElementTypeManager.build('.hiprintEpContainer', matchedProvider.value)
            this.fieldPanelLoaded = true
          } else {
            console.warn('[TemplateDesignerV2] PrintElementTypeManager.build not available')
            this.renderSimpleFieldPanel(container)
          }
        } else {
          console.warn('[TemplateDesignerV2] No provider found for:', this.selectedBillType, this.billType)
          this.renderSimpleFieldPanel(container)
        }
      } catch (error) {
        console.error('[TemplateDesignerV2] Error building field panel:', error)
        this.renderSimpleFieldPanel(container)
      }
    },
    
    /**
     * 回退到默认的字段面板构建方法
     */
    buildFieldPanelDefault(hiprint, container) {
      console.log('[TemplateDesignerV2] Using default field panel method')
      
      try {
        // 方法1: 使用 PrintElementTypeManager.build
        if (hiprint.PrintElementTypeManager && typeof hiprint.PrintElementTypeManager.build === 'function') {
          console.log('[TemplateDesignerV2] Using PrintElementTypeManager.build')
          hiprint.PrintElementTypeManager.build('.hiprintEpContainer', 'defaultModule')
          this.fieldPanelLoaded = true
        } 
        // 方法2: 使用 addPrintElementTypes 后手动渲染
        else if (typeof hiprint.addPrintElementTypes === 'function') {
          console.log('[TemplateDesignerV2] Using addPrintElementTypes approach')
          hiprint.addPrintElementTypes(this.selectedBillType, [])
          
          // 尝试使用 buildFieldPanelForTemplate
          if (this.template && typeof this.template.buildFieldPanelForTemplate === 'function') {
            this.template.buildFieldPanelForTemplate('.hiprintEpContainer', this.selectedBillType)
          }
          this.fieldPanelLoaded = true
        } 
        // 方法3: 查找其他可用方法
        else {
          console.warn('[TemplateDesignerV2] No known method for building field panel')
          console.log('[TemplateDesignerV2] Available hiprint methods:', Object.keys(hiprint).filter(k => typeof hiprint[k] === 'function'))
          // 渲染一个简单的字段面板
          this.renderSimpleFieldPanel(container)
        }
      } catch (error) {
        console.error('[TemplateDesignerV2] buildFieldPanelDefault error:', error)
        this.renderSimpleFieldPanel(container)
      }
    },
    
    /**
     * 将后端字段注册到 hiprint
     */
    registerFieldsToHiprint(hiprint, fieldsData) {
      console.log('[TemplateDesignerV2] registerFieldsToHiprint')
      
      try {
        // 准备 hiprint 格式的字段定义
        const hiprintFields = []
        
        // 处理公司信息字段
        fieldsData.companyFields.forEach(field => {
          hiprintFields.push(this.convertToHiprintField(field, '公司信息'))
        })
        
        // 处理单据主体字段
        fieldsData.headerFields.forEach(field => {
          hiprintFields.push(this.convertToHiprintField(field, '单据主体'))
        })
        
        // 处理单据明细字段
        fieldsData.tableColumns.forEach(field => {
          hiprintFields.push(this.convertToHiprintField(field, '单据明细'))
        })
        
        console.log('[TemplateDesignerV2] Converted fields:', hiprintFields.length)
        
        // 注册到 hiprint
        if (typeof hiprint.addPrintElementTypes === 'function') {
          hiprint.addPrintElementTypes(this.selectedBillType, hiprintFields)
          console.log('[TemplateDesignerV2] Fields registered via addPrintElementTypes')
        }
        
        // 也尝试注册到 PrintElementTypeManager
        if (hiprint.PrintElementTypeManager && typeof hiprint.PrintElementTypeManager.add === 'function') {
          hiprint.PrintElementTypeManager.add(this.selectedBillType, hiprintFields)
          console.log('[TemplateDesignerV2] Fields registered via PrintElementTypeManager.add')
        }
        
      } catch (error) {
        console.error('[TemplateDesignerV2] registerFieldsToHiprint error:', error)
      }
    },
    
    /**
     * 将后端字段转换为 hiprint 格式
     */
    convertToHiprintField(field, group) {
      const hiprintType = field.hiprintType || 'text'
      const hiprintOptions = field.hiprintOptions ? (typeof field.hiprintOptions === 'string' ? JSON.parse(field.hiprintOptions) : field.hiprintOptions) : {}
      
      return {
        tid: field.fieldCode,
        title: field.fieldName,
        group: group,
        type: hiprintType,
        options: hiprintOptions,
        field: hiprintOptions.field || field.fieldCode,
        testData: hiprintOptions.testData || `@${field.fieldName}`
      }
    },
    
    /**
     * 渲染字段面板 UI
     */
    renderFieldPanelUI(container, fieldsData) {
      console.log('[TemplateDesignerV2] renderFieldPanelUI')
      
      try {
        // 检查 hiprint 是否有内置的字段面板渲染方法
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
        
        if (hiprint && hiprint.PrintElementTypeManager && typeof hiprint.PrintElementTypeManager.build === 'function') {
          // 使用 hiprint 内置的方法渲染
          hiprint.PrintElementTypeManager.build('.hiprintEpContainer', 'defaultModule')
          console.log('[TemplateDesignerV2] Field panel rendered via PrintElementTypeManager.build')
          return
        }
        
        // 手动渲染字段面板 (如果 hiprint 没有内置方法)
        this.renderCustomFieldPanel(container, fieldsData)
        
      } catch (error) {
        console.error('[TemplateDesignerV2] renderFieldPanelUI error:', error)
        this.renderCustomFieldPanel(container, fieldsData)
      }
    },
    
    /**
     * 自定义渲染字段面板
     */
    renderCustomFieldPanel(container, fieldsData) {
      console.log('[TemplateDesignerV2] renderCustomFieldPanel')
      
      const groups = [
        { name: '公司信息', fields: fieldsData.companyFields },
        { name: '单据主体', fields: fieldsData.headerFields },
        { name: '单据明细', fields: fieldsData.tableColumns }
      ]
      
      // 创建可折叠的分组
      groups.forEach(group => {
        if (!group.fields || group.fields.length === 0) return
        
        const groupDiv = document.createElement('div')
        groupDiv.className = 'field-group'
        groupDiv.style.marginBottom = '10px'
        
        const headerDiv = document.createElement('div')
        headerDiv.className = 'field-group-header'
        headerDiv.style.cssText = 'cursor:pointer;padding:6px 8px;background:#f0f0f0;border:1px solid #e8e8e8;font-weight:bold;font-size:12px;display:flex;justify-content:space-between;align-items:center;'
        headerDiv.innerHTML = `<span>${group.name}</span><span class="toggle-icon">▼</span>`
        
        const contentDiv = document.createElement('div')
        contentDiv.className = 'field-group-content'
        contentDiv.style.cssText = 'border:1px solid #e8e8e8;border-top:none;padding:4px;background:#fff;'
        
        // 添加字段
        group.fields.forEach(field => {
          const fieldItem = document.createElement('div')
          fieldItem.className = 'field-item'
          fieldItem.draggable = true
          fieldItem.style.cssText = 'padding:4px 6px;margin:2px 0;background:#fff;border:1px solid #d9d9d9;border-radius:3px;cursor:grab;font-size:12px;display:flex;align-items:center;gap:6px;transition:all 0.2s;'
          fieldItem.innerHTML = `
            <span style="color:#1890ff;">${field.fieldName}</span>
          `
          
          // 拖拽事件
          fieldItem.addEventListener('dragstart', (e) => {
            e.dataTransfer.setData('text/plain', JSON.stringify({
              fieldCode: field.fieldCode,
              fieldName: field.fieldName,
              hiprintType: field.hiprintType,
              hiprintOptions: field.hiprintOptions
            }))
            fieldItem.style.opacity = '0.5'
          })
          
          fieldItem.addEventListener('dragend', () => {
            fieldItem.style.opacity = '1'
          })
          
          // 双击添加到画布
          fieldItem.addEventListener('dblclick', () => {
            this.addFieldToCanvas(field)
          })
          
          // hover 效果
          fieldItem.addEventListener('mouseenter', () => {
            fieldItem.style.backgroundColor = '#e6f7ff'
            fieldItem.style.borderColor = '#1890ff'
          })
          fieldItem.addEventListener('mouseleave', () => {
            fieldItem.style.backgroundColor = '#fff'
            fieldItem.style.borderColor = '#d9d9d9'
          })
          
          contentDiv.appendChild(fieldItem)
        })
        
        // 折叠/展开
        headerDiv.addEventListener('click', () => {
          const isVisible = contentDiv.style.display !== 'none'
          contentDiv.style.display = isVisible ? 'none' : 'block'
          headerDiv.querySelector('.toggle-icon').textContent = isVisible ? '▶' : '▼'
        })
        
        groupDiv.appendChild(headerDiv)
        groupDiv.appendChild(contentDiv)
        container.appendChild(groupDiv)
      })
      
      console.log('[TemplateDesignerV2] Custom field panel rendered')
    },
    
    /**
     * 渲染简单的字段面板（用于紧急回退）
     */
    renderSimpleFieldPanel(container) {
      const tip = document.createElement('div')
      tip.style.cssText = 'padding:10px;color:#999;font-size:12px;text-align:center;'
      tip.textContent = '字段加载失败，请检查网络连接'
      container.appendChild(tip)
    },
    
    /**
     * 将字段添加到画布
     */
    addFieldToCanvas(field) {
      console.log('[TemplateDesignerV2] addFieldToCanvas:', field)
      
      if (!this.template) {
        this.$message.warning('设计器未初始化')
        return
      }
      
      try {
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
        
        if (!hiprint) {
          this.$message.error('hiprint 未加载')
          return
        }
        
        const hiprintOptions = field.hiprintOptions ? (typeof field.hiprintOptions === 'string' ? JSON.parse(field.hiprintOptions) : field.hiprintOptions) : {}
        
        // 创建新元素的配置
        const newElement = {
          tid: field.fieldCode,
          options: {
            left: 100,
            top: 100,
            width: 100,
            height: 20,
            field: hiprintOptions.field || field.fieldCode,
            title: hiprintOptions.field || field.fieldName,
            testData: hiprintOptions.testData || `@${field.fieldName}`,
            font: 'SimSun',
            fontSize: 12,
            fontWeight: 'normal',
            color: '#000000',
            textAlign: 'left',
            ...hiprintOptions
          }
        }
        
        // 尝试添加到模板
        if (typeof this.template.addElement === 'function') {
          this.template.addElement(newElement)
        } else if (typeof this.template.addPrintElement === 'function') {
          this.template.addPrintElement(newElement)
        } else {
          console.warn('[TemplateDesignerV2] No method to add element to template')
          this.$message.info('请从左侧面板拖拽字段到画布')
        }
        
      } catch (error) {
        console.error('[TemplateDesignerV2] addFieldToCanvas error:', error)
        this.$message.error('添加字段失败')
      }
    },
    
    /**
     * 设置元素选择监听
     */
    setupElementSelection() {
      // 使用 DOM 点击委托追踪选择变化（替代 hiprint 不可靠的内部事件）
      const container = document.getElementById('hiprint-printTemplate')
      if (!container) return

      const updateCount = () => {
        if (this.template && typeof this.template.getSelectEls === 'function') {
          try {
            const els = this.template.getSelectEls()
            this.selectedCount = els ? els.length : 0
          } catch (e) {
            // ignore
          }
        }
      }

      // 点击后延迟更新（等待 hiprint 处理完点击）
      container.addEventListener('click', () => {
        setTimeout(updateCount, 100)
      })

      // 鼠标松开后也更新（处理框选拖拽）
      container.addEventListener('mouseup', () => {
        setTimeout(updateCount, 100)
      })

      // 初始更新
      updateCount()
    },
    
    /**
     * 自动选中第一个元素
     */
    autoSelectFirstElement() {
      console.log('[TemplateDesignerV2] autoSelectFirstElement')
      
      if (!this.template) return
      
      try {
        // 等待一下让DOM渲染
        setTimeout(() => {
          // 尝试查找第一个可编辑元素
          const elements = document.querySelectorAll('#hiprint-printTemplate .hiprint-printElement')
          console.log('[TemplateDesignerV2] found elements:', elements.length)
          
          if (elements.length > 0) {
            // 选中第一个
            const firstElement = elements[0]
            firstElement.dispatchEvent(new MouseEvent('click', { bubbles: true }))
            this.selectedCount = 1
          }
        }, 300)
      } catch (error) {
        console.warn('autoSelectFirstElement error:', error)
      }
    },
    
    /**
     * 获取纸张类型key
     */
    getPaperTypeKey(width, height) {
      for (const [key, paper] of Object.entries(this.paperTypes)) {
        if (Math.abs(paper.width - width) < 1 && Math.abs(paper.height - height) < 1) {
          return key
        }
      }
      return 'custom'
    },
    
    /**
     * 设置纸张
     */
    setPaper(type, paper) {
      if (paper) {
        this.curPaper = { type: type, width: paper.width, height: paper.height }
      } else {
        this.curPaper = { type: type, width: this.paperWidth, height: this.paperHeight }
      }

      if (this.template && typeof this.template.setPaper === 'function') {
        this.template.setPaper(this.curPaper.width, this.curPaper.height)
      }
      this.$nextTick(() => {
        this.applyGridOverlay()
        this.applyMarginOverlay()
      })
    },
    
    /**
     * 显示自定义纸张弹窗
     */
    showCustomPaperModal() {
      this.paperWidth = this.curPaper.width
      this.paperHeight = this.curPaper.height
      this.customPaperModalVisible = true
    },
    
    /**
     * 应用自定义纸张
     */
    applyCustomPaper() {
      if (this.paperWidth && this.paperHeight) {
        this.setPaper('custom', null)
        this.$nextTick(() => {
          this.applyMarginOverlay()
        })
        this.customPaperModalVisible = false
      }
    },
    
    /**
     * 改变缩放
     */
    changeScale(increase) {
      let newScale = this.scaleValue
      if (increase) {
        newScale = Math.min(this.scaleMax, newScale + 0.1)
      } else {
        newScale = Math.max(this.scaleMin, newScale - 0.1)
      }
      
      this.scaleValue = newScale
      
      if (this.template && typeof this.template.zoom === 'function') {
        this.template.zoom(newScale, true)
      }
    },
    
    /**
     * 全选
     */
    selectAll() {
      if (!this.template) {
        this.$message.warning('设计器未初始化')
        return
      }
      
      try {
        if (typeof this.template.selectAll === 'function') {
          this.template.selectAll()
        } else if (typeof this.template.selectAllElements === 'function') {
          this.template.selectAllElements()
        } else {
          // 手动选中所有元素
          const elements = document.querySelectorAll('#hiprint-printTemplate .hiprint-printElement')
          elements.forEach((el, index) => {
            if (index === 0) {
              el.dispatchEvent(new MouseEvent('click', { bubbles: true }))
            }
          })
        }
        this.$nextTick(() => {
          if (this.template && typeof this.template.getSelectEls === 'function') {
            const els = this.template.getSelectEls()
            this.selectedCount = els ? els.length : 0
          }
        })
      } catch (error) {
        console.warn('全选失败', error)
      }
    },
    
    /**
     * 对齐选中元素
     */
    alignElements(type) {
      if (!this.template) {
        this.$message.warning('设计器未初始化')
        return
      }
      // 兜底刷新 selectedCount（防止事件未触发）
      try {
        const els = this.template.getSelectEls && this.template.getSelectEls()
        if (els) this.selectedCount = els.length
      } catch (e) { /* ignore */ }
      try {
        this.template.setElsAlign(type)
      } catch (e) {
        console.error('对齐操作失败:', e)
        this.$message.error('对齐失败')
      }
    },
    
    /**
     * 网格设置变更
     */
    onGridSettingsChange() {
      this.applyGridOverlay()
      this.saveGridSettingsToLocal()
    },
    saveGridSettingsToLocal() {
      const key = 'print_template_grid_' + (this.selectedBillType || 'default')
      localStorage.setItem(key, JSON.stringify({
        show: this.gridShow,
        size: this.gridSize
      }))
    },
    loadGridSettingsFromLocal() {
      const key = 'print_template_grid_' + (this.selectedBillType || 'default')
      try {
        const stored = localStorage.getItem(key)
        if (stored) {
          const settings = JSON.parse(stored)
          this.gridShow = settings.show || false
          this.gridSize = settings.size || 8
        }
      } catch (e) {
        // ignore
      }
    },
    
    /**
     * 保存页眉页脚位置到本地
     */
    saveHeaderFooterToLocal() {
      const key = 'print_template_headerfooter_' + (this.selectedBillType || 'default')
      if (this.template && this.template.editingPanel) {
        const panel = this.template.editingPanel
        localStorage.setItem(key, JSON.stringify({
          paperHeader: panel.paperHeader || 0,
          paperFooter: panel.paperFooter || 0
        }))
      }
    },
    
    /**
     * 从本地加载页眉页脚位置
     * @returns {boolean} 是否成功加载
     */
    loadHeaderFooterFromLocal() {
      const key = 'print_template_headerfooter_' + (this.selectedBillType || 'default')
      try {
        const stored = localStorage.getItem(key)
        if (!stored) return false
        const settings = JSON.parse(stored)
        if (this.template && this.template.editingPanel) {
          const panel = this.template.editingPanel
          if (settings.paperHeader) {
            panel.paperHeader = settings.paperHeader
            if (panel.designPaper && panel.designPaper.headerLinetarget) {
              panel.designPaper.headerLinetarget.css('top', settings.paperHeader + 'pt')
              panel.designPaper.headerLinetarget.removeClass('hideheaderLinetarget')
            }
          }
          if (settings.paperFooter) {
            panel.paperFooter = settings.paperFooter
            if (panel.designPaper && panel.designPaper.footerLinetarget) {
              panel.designPaper.footerLinetarget.css('top', settings.paperFooter + 'pt')
              panel.designPaper.footerLinetarget.removeClass('hidefooterLinetarget')
            }
          }
          return true
        }
      } catch (e) {
        // ignore
      }
      return false
    },
    applyGridOverlay() {
      const container = document.querySelector('#hiprint-printTemplate .hiprint-printPaper')
      if (!container) return
      
      // 移除旧网格
      const oldGrid = container.querySelector('.custom-grid-overlay')
      if (oldGrid) oldGrid.remove()
      
      if (!this.gridShow) return
      
      // 创建网格覆盖层
      const gridOverlay = document.createElement('div')
      gridOverlay.className = 'custom-grid-overlay'
      gridOverlay.style.cssText = [
        'position: absolute; top: 0; left: 0; right: 0; bottom: 0;',
        'pointer-events: none; z-index: 1;',
        'background-image:',
        '  linear-gradient(rgba(0,0,0,0.08) 1px, transparent 1px),',
        '  linear-gradient(90deg, rgba(0,0,0,0.08) 1px, transparent 1px);',
        'background-size: ' + this.gridSize + 'mm ' + this.gridSize + 'mm;'
      ].join('\n')
      container.appendChild(gridOverlay)
    },
    
    /**
     * 页边距变更
     */
    onMarginChange() {
      this.applyMarginOverlay()
    },
    applyMarginOverlay() {
      const container = document.querySelector('#hiprint-printTemplate .hiprint-printPaper')
      if (!container) return
      
      // 移除旧边框
      const oldBorder = container.querySelector('.custom-margin-overlay')
      if (oldBorder) oldBorder.remove()
      
      const mTop = this.marginTop || 0
      const mBottom = this.marginBottom || 0
      const mLeft = this.marginLeft || 0
      const mRight = this.marginRight || 0
      
      if (mTop === 0 && mBottom === 0 && mLeft === 0 && mRight === 0) return
      
      // 创建页边距边框覆盖层
      const marginOverlay = document.createElement('div')
      marginOverlay.className = 'custom-margin-overlay'
      const paperWidth = this.curPaper.width
      const paperHeight = this.curPaper.height
      marginOverlay.style.cssText = [
        'position: absolute;',
        'top: ' + mTop + 'mm;',
        'left: ' + mLeft + 'mm;',
        'width: ' + (paperWidth - mLeft - mRight) + 'mm;',
        'height: ' + (paperHeight - mTop - mBottom) + 'mm;',
        'border: 1px solid #52c41a;',
        'pointer-events: none;',
        'z-index: 2;'
      ].join('\n')
      container.appendChild(marginOverlay)
    },

    /**
     * 显示JSON编辑弹窗
     */
    showJsonModal() {
      if (!this.template) return
      
      try {
        // 获取模板 JSON - 尝试多个方法名
        let templateJSON
        if (typeof this.template.getJson === 'function') {
          templateJSON = this.template.getJson()
        } else if (typeof this.template.getJSON === 'function') {
          templateJSON = this.template.getJSON()
        } else {
          throw new Error('无法获取模板数据')
        }
        
        // 使用 replacer 处理循环引用
        this.templateJsonText = JSON.stringify(templateJSON, (key, value) => {
          if (key === 'panel' || key === 'parent' || key === 'self' || key === '_parent') {
            return undefined
          }
          return value
        }, this.jsonCompressed ? 0 : 2)
        
        this.jsonModalVisible = true
      } catch (error) {
        console.warn('获取模板JSON失败', error)
        this.$message.error('获取模板JSON失败')
      }
    },
    
    /**
     * 格式化JSON
     */
    formatJson() {
      try {
        const json = JSON.parse(this.templateJsonText)
        this.templateJsonText = JSON.stringify(json, null, 2)
        this.jsonCompressed = false
      } catch (e) {
        this.$message.error('JSON格式错误')
      }
    },
    
    /**
     * 压缩JSON
     */
    compressJson() {
      try {
        const json = JSON.parse(this.templateJsonText)
        this.templateJsonText = JSON.stringify(json)
        this.jsonCompressed = true
      } catch (e) {
        this.$message.error('JSON格式错误')
      }
    },
    
    /**
     * 应用JSON
     */
    applyJson() {
      try {
        const json = JSON.parse(this.templateJsonText)
        
        // 先销毁旧模板
        if (this.template) {
          try {
            if (this.template.destroy) {
              this.template.destroy()
            }
            this.template = null
          } catch (e) {
            console.warn('销毁旧模板失败', e)
          }
        }
        
        // 清空容器
        const container = document.querySelector('#hiprint-printTemplate')
        if (container) {
          container.innerHTML = ''
        }
        
        // 再创建新模板
        this.initHiprintDesigner(json)
        this.jsonModalVisible = false
        this.$message.success('模板已应用')
      } catch (e) {
        this.$message.error('JSON格式错误')
      }
    },
    
    /**
     * 恢复默认模板
     */
    handleReset() {
      const billTypeName = getBillTypeName(this.selectedBillType)
      const defaultTemplate = getDefaultTemplateByName(billTypeName) || getDefaultTemplateByCode(this.selectedBillType)
      
      if (!defaultTemplate) {
        this.$message.warning('未找到默认模板')
        return
      }
      
      try {
        // 先销毁旧模板
        if (this.template) {
          try {
            if (this.template.destroy) {
              this.template.destroy()
            }
            this.template = null
          } catch (e) {
            console.warn('销毁旧模板失败', e)
          }
        }
        
        // 清空容器
        const container = document.querySelector('#hiprint-printTemplate')
        if (container) {
          container.innerHTML = ''
        }
        
        // 再创建新模板
        this.initHiprintDesigner(defaultTemplate)
        this.currentTemplateName = billTypeName + '（默认）'
        this.$message.success('已恢复默认模板')
      } catch (error) {
        console.error('恢复默认模板失败', error)
        this.$message.error('恢复默认模板失败: ' + error.message)
      }
    },
    
    /**
     * 刷新
     */
    handleRefresh() {
      this.reinitialize()
    },
    
    /**
     * 清空
     */
    handleClear() {
      if (this.template && typeof this.template.clear === 'function') {
        this.template.clear()
        this.selectedCount = 0
      }
    },
    
    /**
     * 保存
     */
    async handleSave() {
      if (!this.template) return
      
      try {
        // 获取模板 JSON - 尝试多个方法名
        let templateJSON
        if (typeof this.template.getJson === 'function') {
          templateJSON = this.template.getJson()
        } else if (typeof this.template.getJSON === 'function') {
          templateJSON = this.template.getJSON()
        } else {
          throw new Error('无法获取模板数据')
        }
        
        // 强制缩放为1，保证打印时100%原始大小
        if (templateJSON.panels && templateJSON.panels[0]) {
          templateJSON.panels[0].scale = 1
        }
        
        // 处理循环引用的 JSON 序列化
        const templateJsonStr = JSON.stringify(templateJSON, (key, value) => {
          // 跳过可能导致循环引用的属性
          if (key === 'panel' || key === 'parent' || key === 'self' || key === '_parent') {
            return undefined
          }
          return value
        })
        
        // 获取 billType 代码和名称
        const billTypeCode = this.selectedBillType || 'QGD'
        const billTypeName = getBillTypeName(billTypeCode)
        
        // 获取当前用户的 tenantId（优先从 Vuex store 获取，回退到 localStorage）
        let currentTenantId = null
        try {
          // 优先从 Vuex store 获取
          const userInfo = this.$store.getters.userInfo || this.$store.state.user.info
          if (userInfo) {
            currentTenantId = userInfo.tenantId
            console.log('[TemplateDesignerV2] 从 Vuex 获取用户信息:', { tenantId: currentTenantId })
          }
          
          // 如果 Vuex 没有，尝试从 localStorage 获取（使用正确的 key）
          if (!currentTenantId) {
            const storedUserInfo = Vue.ls.get('Login_Userinfo')
            if (storedUserInfo) {
              currentTenantId = storedUserInfo.tenantId
              console.log('[TemplateDesignerV2] 从 localStorage 获取用户信息:', { tenantId: currentTenantId })
            }
          }
        } catch (e) {
          console.warn('[TemplateDesignerV2] 获取用户租户ID失败:', e.message)
        }
        console.log('[TemplateDesignerV2] 最终确定用户 tenantId:', currentTenantId)
        
        // 判断是否为系统模板（tenantId 为 null 或 0 表示系统模板，保存时需要新增）
        const isSystemTemplate = (
          this.templateTenantId === 0 || 
          this.templateTenantId === null || 
          this.templateTenantId === undefined
        )
        console.log('[TemplateDesignerV2] 判断模板类型:', {
          templateTenantId: this.templateTenantId,
          isSystemTemplate: isSystemTemplate,
          currentTenantId: currentTenantId
        })
        
        // 构建保存数据（显式传递 tenantId）
        const templateData = {
          templateName: this.currentTemplateName || (billTypeName + '模板'),
          billType: billTypeCode,
          templateJson: templateJsonStr,
          isDefault: 0,
          tenantId: currentTenantId,  // 显式传递当前租户ID
          // 纸张大小
          paperType: this.curPaper.type,
          paperWidth: this.curPaper.width,
          paperHeight: this.curPaper.height,
          // 页边距
          marginTop: this.marginTop || 0,
          marginRight: this.marginRight || 0,
          marginBottom: this.marginBottom || 0,
          marginLeft: this.marginLeft || 0
        }
        
        // 只有当是租户自己的模板时才传递 id 进行更新
        // 如果是系统模板（tenantId 为 0 或 null），则新增租户模板，不传递 id
        if (!isSystemTemplate && this.selectedTemplate) {
          templateData.id = this.selectedTemplate
          console.log('[TemplateDesignerV2] 更新现有模板，id:', this.selectedTemplate)
        } else {
          console.log('[TemplateDesignerV2] 系统模板或无模板，新增租户模板')
        }
        
        console.log('[TemplateDesignerV2] handleSave templateData:', {
          billType: templateData.billType,
          templateName: templateData.templateName,
          hasId: templateData.id !== undefined,
          tenantId: templateData.tenantId
        })
        
        const result = await saveBillPrintTemplate(templateData)
        
        if (result && result.code === 200) {
          this.$message.success('保存成功')
          
          // 保存页眉页脚位置到本地
          this.saveHeaderFooterToLocal()
          
          // 触发保存成功事件
          this.$emit('saved', result.data)
          
          // 更新当前模板名称
          if (result.data && result.data.templateName) {
            this.currentTemplateName = result.data.templateName
          }
          
          // 更新当前模板ID
          if (result.data && result.data.id) {
            this.selectedTemplate = result.data.id
            this.$emit('update:templateId', result.data.id)
          }
          
          // 如果是新增模板（原来是系统模板），更新本地 tenantId
          if (isSystemTemplate && result.data && result.data.tenantId) {
            this.templateTenantId = result.data.tenantId
            console.log('[TemplateDesignerV2] 新增模板后更新 templateTenantId:', this.templateTenantId)
          }
        } else {
          this.$message.error((result && result.msg) || '保存失败')
        }
      } catch (error) {
        console.error('保存失败', error)
        this.$message.error('保存失败: ' + (error.message || '未知错误'))
      }
    },
    
    /**
     * 打印预览 - 直接调用浏览器原生打印对话框
     */
    async handlePreview() {
      if (!this.template) {
        this.$message.warning('设计器未初始化')
        return
      }
      try {
        let previewData
        if (this.billData && typeof this.billData === 'object' && Object.keys(this.billData).length > 0) {
          previewData = this.billData
        } else {
          previewData = this.getDefaultPreviewData()
        }
        // [DEBUG] 输出模板JSON和数据用于对比
        console.log('[TemplateDesignerV2] === 打印预览调试信息 ===')
        console.log('[TemplateDesignerV2] billType:', this.billType, 'selectedBillType:', this.selectedBillType)
        console.log('[TemplateDesignerV2] template JSON:', JSON.stringify(this.template.getJson ? this.template.getJson() : 'N/A'))
        console.log('[TemplateDesignerV2] previewData keys:', Object.keys(previewData))
        console.log('[TemplateDesignerV2] previewData:', JSON.stringify(previewData, null, 2))
        // 输出 getHtml 结果
        const htmlResult = this.template.getHtml(previewData)
        if (htmlResult && htmlResult[0]) {
          console.log('[TemplateDesignerV2] getHtml outerHTML (前2000字符):', htmlResult[0].outerHTML ? htmlResult[0].outerHTML.substring(0, 2000) : 'N/A')
          console.log('[TemplateDesignerV2] getHtml panel尺寸:', htmlResult[0].style ? { width: htmlResult[0].style.width, height: htmlResult[0].style.height } : 'N/A')
        }
        console.log('[TemplateDesignerV2] === 调试信息结束 ===')
        if (typeof this.template.print === 'function') {
          this.template.print(previewData)
        } else {
          this.$message.error('打印功能不可用')
        }
      } catch (error) {
        console.error('打印失败', error)
        this.$message.error('打印失败')
      }
    },
    
    /**
     * 获取默认预览数据
     */
    getDefaultPreviewData() {
      // 根据不同的单据类型返回默认数据
      const billType = this.selectedBillType
      
      const defaultDataMap = {
        'QGD': {
          billTitle: '请购单',
          number: 'QGD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例供应商',
          address: '示例地址',
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'CGDD': {
          billTitle: '采购订单',
          number: 'CGDD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例供应商',
          address: '示例地址',
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 },
            { rowIndex: 2, name: '商品B', spec: '规格B', unit: '箱', basicNumber: 50, price: 20, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'CGRK': {
          billTitle: '采购入库',
          number: 'CGRK2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例供应商',
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'XSDD': {
          billTitle: '销售订单',
          number: 'XSDD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例客户',
          remark: '示例备注',
          billDetailAllPrice: '250.00',
          billDetailCnMoney: '贰佰伍拾元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 15, basicTotal: 1500 },
            { rowIndex: 2, name: '商品B', spec: '规格B', unit: '箱', basicNumber: 50, price: 25, basicTotal: 1250 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'XSCK': {
          billTitle: '销售出库',
          number: 'XSCK2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例客户',
          remark: '示例备注',
          billDetailAllPrice: '250.00',
          billDetailCnMoney: '贰佰伍拾元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 15, basicTotal: 1500 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'DBCK': {
          billTitle: '调拨出库',
          number: 'DBCK2024001',
          operTimeStr: new Date().toLocaleDateString(),
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'ZZD': {
          billTitle: '组装单',
          number: 'ZZD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'CXD': {
          billTitle: '拆卸单',
          number: 'CXD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        }
      }
      
      return defaultDataMap[billType] || {
        billTitle: billType + '单据',
        number: billType + '2024001',
        operTimeStr: new Date().toLocaleDateString(),
        remark: '示例备注',
        billDetails: [],
        company: { companyName: '示例公司' }
      }
    },
    
    /**
     * 返回
     */
    handleBack() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.template-designer-v2 {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f5;
  overflow: hidden;
}

.template-designer-v2.embedded-mode {
  height: 100%;
}

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  padding: 5px 5px;
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  gap: 5px;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.toolbar-center {
  display: flex;
  align-items: center;
  gap: 5px;
  flex: none;
  flex-shrink: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.zoom-control {
  display: flex;
  align-items: center;
  gap: 5px;
}

.scale-display {
  min-width: 48px;
  text-align: center;
  font-size: 13px;
  color: #333;
}

/* 主内容区 */
.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 左侧面板 */
.left-panel {
  width: 200px;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 10px 12px;
  font-weight: bold;
  font-size: 13px;
  color: #333;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.hiprintEpContainer {
  /* hiprint 自动填充 */
}

.loading-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #999;
  font-size: 12px;
}

/* 中间画布区 */
.center-panel {
  flex: 1;
  overflow: auto;
  padding: 20px;
  background: #e8e8e8;
  position: relative;
}

.ruler {
  height: 20px;
  background: #f0f0f0;
  border-bottom: 1px solid #d9d9d9;
  margin-bottom: 10px;
}

.hiprint-printTemplate {
  background: #fff;
  min-height: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
}

.empty-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* 右侧面板 */
.right-panel {
  width: 280px;
  background: #fff;
  border-left: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.PrintElementOptionSetting {
  /* hiprint 自动填充 */
}

.selection-hint {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 12px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
}

/* 自定义纸张表单 */
.custom-paper-form {
  padding: 10px 0;
}

.custom-paper-form .form-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.custom-paper-form .form-item label {
  width: 100px;
  margin-right: 12px;
  color: #666;
}

.custom-paper-form .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

/* JSON 弹窗 */
.json-modal-content {
  display: flex;
  flex-direction: column;
}

.json-toolbar {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.json-modal-footer {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* 页眉线 - 红色虚线 */
.center-panel >>> .hiprint-headerLine {
  border-top-color: #ff4d4f !important;
  border-top-style: dashed !important;
}
.center-panel >>> .hiprint-headerLine:hover::after {
  content: '页眉线';
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4d4f;
  color: #fff;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 100;
}

/* 页脚线 - 红色虚线 */
.center-panel >>> .hiprint-footerLine {
  border-top-color: #ff4d4f !important;
  border-top-style: dashed !important;
}
.center-panel >>> .hiprint-footerLine:hover::after {
  content: '页脚线';
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4d4f;
  color: #fff;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 100;
}
</style>
